import type { AnyRouter } from '@tanstack/react-router';

// Client-side route sync with the monday host.
//
// In the monday iframe the app is a static CDN bundle with no SPA fallback (only '/' resolves), so
// the router runs on an in-memory history seeded from the ?vibe-route= param the host injects on
// load (see getInitialRoute + router.tsx). This module mirrors in-app navigation up to the host and
// applies host-driven navigation back down, matching the @mondaydotcomorg/vibe-route-sync
// postMessage contract (the same one the shadcn/JS template uses via router-sync.js).

const VIBE_ROUTE_PARAM = 'vibe-route';
const MSG_ROUTE_CHANGE = 'vibe:route-change';
const MSG_ROUTE_UPDATE = 'vibe:route-update';

export function isInIframe(): boolean {
  return typeof window !== 'undefined' && window.self !== window.top;
}

// Initial in-memory location, taken from the ?vibe-route= param the host seeds. Defaults to '/'.
export function getInitialRoute(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  const vibeRoute = new URLSearchParams(window.location.search).get(VIBE_ROUTE_PARAM);
  return vibeRoute || '/';
}

function getParentOrigin(): string {
  try {
    return new URL(document.referrer).origin;
  } catch {
    return '*';
  }
}

let attached = false;

// Bridge in-app navigation to the monday host and accept host-driven navigation. Idempotent and
// browser-only; a no-op outside the monday iframe (standalone/dev uses real browser URLs).
export function attachVibeRouteSync(router: AnyRouter): void {
  if (attached || !isInIframe()) {
    return;
  }
  attached = true;

  const parentOrigin = getParentOrigin();
  // Echo guard: when we apply a host-driven navigation, don't post it straight back to the host.
  let applyingHostNavigation = false;

  router.subscribe('onResolved', () => {
    if (applyingHostNavigation) {
      applyingHostNavigation = false;
      return;
    }
    window.parent.postMessage({ type: MSG_ROUTE_CHANGE, path: router.history.location.href }, parentOrigin);
  });

  window.addEventListener('message', (event: MessageEvent) => {
    if (parentOrigin !== '*' && event.origin !== parentOrigin) {
      return;
    }
    const data = event.data as { type?: string; path?: string } | null;
    if (data?.type === MSG_ROUTE_UPDATE && typeof data.path === 'string') {
      applyingHostNavigation = true;
      router.history.replace(data.path);
    }
  });
}
